import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import Modal from 'live/components/ui/Modal'
import SelectOptions from './SelectOptions'
import { cloneDeep } from 'lodash'
import '../main/options/modalFull.scss'
import { Group, GroupModel } from './interface/Group'
import { createGroupModel, updateGroupModel } from '../../api/groupDatabases'
import LiveTooltip from 'live/components/ui/tooltip'
import i18n from '../../services/i18n'
import globalNotification from 'live/services/global-notification'
import { databaseHasDateReference, existEmptyFields, exitstWrongFormatedDateReference } from './utils'
import { UpdateContext } from '../GroupDatabase'
import { DatabaseModel } from './interface/Group'
import MaskedInput from 'react-maskedinput'

interface Props {
    show: boolean
    setShowNewGroupModal: (show: boolean) => void
    databaseModels: DatabaseModel[]
    mode: 'edit' | 'save'
    groupModel?: GroupModel
}

export default function NewDatabaseGroupDialog({ show, setShowNewGroupModal, mode, groupModel }: Props) {
    const [databaseGroupName, setDatabaseGroupName] = useState<string>('')
    const [databases, setDatabases] = useState<Group[]>([])
    const [disableSave, setDisableSave] = useState<boolean>(true)

    useEffect(() => {
        setDisableSave(existEmptyFields(databases, databaseGroupName, databaseModels))
    }, [databases, databaseGroupName])

    useEffect(() => {
        if (mode === 'edit') {
            setDatabaseGroupName(groupModel.name)
            setDatabases(groupModel.templates)
        }
    }, [groupModel])

    const { updateData, databaseModels } = useContext(UpdateContext)

    const handleCancel = () => {
        if (mode === 'save') {
            let updatedDatabases = cloneDeep(databases)
            updatedDatabases = []
            setDatabases(updatedDatabases)
        }

        setShowNewGroupModal(false)
    }

    const handleSave = () => {
        if (exitstWrongFormatedDateReference(databases, databaseModels)) {
            globalNotification.error('One or more templates have wrong formatting in the reference date (YYYY-mm-dd)')
            return
        }

        const postParameters: GroupModel = {
            name: databaseGroupName.trim(),
            uid: groupModel ? groupModel.uid : undefined,
            templates: databases
        }
        let updatedDatabases = cloneDeep(databases)

        mode === 'save'
            ? createGroupModel(postParameters).then(() => {
                  updateData()
              })
            : updateGroupModel(postParameters).then(() => {
                  updateData()
              })

        if (mode === 'save') {
            updatedDatabases = []
            setDatabases(updatedDatabases)
            setDatabaseGroupName('')
        }
        setShowNewGroupModal(false)
        globalNotification.success(
            <AlertContainer>
                <div className="title">
                    <i class="far fa-check-circle"></i>
                    {`Database Group was successfully ${mode === 'save' ? 'created' : 'updated'} .`}
                </div>
                <div className="subTitle">
                    {`"${databaseGroupName} was ${mode === 'save' ? 'created' : 'updated'} with ${
                        databases.length
                    } databases.`}
                </div>
            </AlertContainer>
        )
    }

    const onDatabaseGroupNameInputName = event => {
        setDatabaseGroupName(event.target.value)
    }

    const addNewDatbase = (event): void => {
        event.preventDefault()
        const emptyDatabase: Group = {
            eventType: '',
            databaseName: '',
            matchFileName: '',
            matchTabName: '',
            refDate: ''
        }
        const updatedDatabases = cloneDeep(databases)
        updatedDatabases.push(emptyDatabase)
        setDatabases(updatedDatabases)
    }

    const onChangeDatabase = (value: string, index: number) => {
        const updatedDatabases: Group[] = cloneDeep(databases)
        updatedDatabases[index].databaseName = value
        updatedDatabases[index].eventType = value
        updatedDatabases[index].refDate = ''
        setDatabases(updatedDatabases)
    }
    const onChangeFileName = (value, index) => {
        const updatedDatabases: Group[] = cloneDeep(databases)
        updatedDatabases[index].matchFileName = value
        setDatabases(updatedDatabases)
    }

    const onChangeTab = (value, index) => {
        const updatedDatabases: Group[] = cloneDeep(databases)
        updatedDatabases[index].matchTabName = value
        setDatabases(updatedDatabases)
    }

    const onChangeReference = (value, index) => {
        const updatedDatabases: Group[] = cloneDeep(databases)
        updatedDatabases[index].refDate = value
        setDatabases(updatedDatabases)
    }

    const onDeleteLine = (event, index) => {
        const updatedDatabases = cloneDeep(databases)
        updatedDatabases.splice(index, 1)
        setDatabases(updatedDatabases)
    }

    return (
        <Modal show={show} backdrop="static" keyboard={false} className="newGroupDatabaseModal">
            <Modal.Header>
                <Modal.Title>
                    <b>{groupModel?.name ?? 'New Database Group'}</b>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DatabaseNameContainer>
                    <div className="title">
                        <b>Database Group Name</b>
                    </div>
                    <input
                        className="input"
                        placeholder=" Name of this database group"
                        value={databaseGroupName}
                        onChange={event => onDatabaseGroupNameInputName(event)}
                    />
                </DatabaseNameContainer>
                <TableDataContainer>
                    <div className="dataContainer">
                        <div className="table">
                            <div className="table--column">
                                <b>Database</b>
                            </div>
                            <div className="table--column">
                                <span>
                                    <b>File Name</b>
                                </span>
                                <LiveTooltip
                                    className="table--column__live-tooltip"
                                    hide={false}
                                    position="top"
                                    content={i18n('RegEx Example: First_Characters_*')}
                                >
                                    <i className="far fa-info-circle" />
                                </LiveTooltip>{' '}
                            </div>
                            <div className="table--column">
                                <span>
                                    <b>Tab </b>
                                    {'(index or ID)'}
                                </span>
                                <LiveTooltip
                                    className="table--column__live-tooltip"
                                    hide={false}
                                    position="top"
                                    content={i18n('Insert Sheet tab name or sheet id.')}
                                >
                                    <i className="far fa-info-circle" />
                                </LiveTooltip>
                            </div>
                            <div className="table--column">
                                <b>Reference Date</b>
                                <LiveTooltip
                                    className="table--column__live-tooltip"
                                    hide={false}
                                    position="top"
                                    content={i18n('Format: dd/mm/AAAA.')}
                                >
                                    <i className="far fa-info-circle" />
                                </LiveTooltip>{' '}
                            </div>
                            <div className="table--lastColumn"></div>
                        </div>
                        <div className="scrollBar">
                            {databases.map((databaseData, index) => (
                                <div className="table">
                                    <div className="table--column__data">
                                        <SelectOptions
                                            index={index}
                                            onChange={onChangeDatabase}
                                            placeholder={'Add Database ...'}
                                            value={databaseData?.databaseName}
                                            options={databaseModels.map(model => {
                                                return model.eventType
                                            })}
                                        />
                                    </div>
                                    <div className="table--column__data">
                                        <input
                                            disabled={databaseData?.databaseName.trim() === ''}
                                            value={databaseData.matchFileName}
                                            placeholder="File Name (RegEx)"
                                            onChange={event => {
                                                onChangeFileName(event.target.value, index)
                                            }}
                                        />
                                    </div>
                                    <div className="table--column__data">
                                        <input
                                            disabled={databaseData?.databaseName.trim() === ''}
                                            value={databaseData.matchTabName}
                                            placeholder="Tab Identificatiion"
                                            onChange={event => {
                                                onChangeTab(event.target.value, index)
                                            }}
                                        />
                                    </div>
                                    <div className="table--column__data">
                                        <MaskedInput
                                            mask="1111-11-11"
                                            type="text"
                                            disabled={
                                                databaseData?.databaseName.trim() === '' ||
                                                !databaseModels ||
                                                !databaseHasDateReference(databaseData?.databaseName, databaseModels)
                                            }
                                            value={databaseData.refDate}
                                            placeholder="YYYY-mm-dd"
                                            onChange={event => {
                                                onChangeReference(event.target.value, index)
                                            }}
                                        />
                                    </div>
                                    <div className="table--lastColumn">
                                        <button onClick={event => onDeleteLine(event, index)}>
                                            <i className="far fa-trash-alt" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            className={'addLine'}
                            onClick={event => {
                                addNewDatbase(event)
                            }}
                        >
                            + Add Database
                        </button>
                    </div>
                </TableDataContainer>
            </Modal.Body>
            <Modal.Footer>
                <FooterContainer>
                    <button
                        onClick={handleSave}
                        className={`button--save${disableSave ? '__disabled' : ''}`}
                        disabled={disableSave}
                        data-cy="saveModel"
                    >
                        Save
                    </button>
                    <button data-cy="cancel" onClick={handleCancel} className={'button--cancel'}>
                        {' '}
                        Cancel
                    </button>
                </FooterContainer>
            </Modal.Footer>
        </Modal>
    )
}

const DatabaseNameContainer = styled.div`
    font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
    height: 100%;
    position: relative;
    align-items: center;
    width: 957px;
    margin-bottom: 24px;
    .title {
        font-size: 20px;
        color: var(--gray-800);
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        margin-bottom: 4px;
    }
    .input {
        width: 352px;
        margin-top: 10px;
        padding-left: 5px;
        border-radius: 3px;
        border: 1px solid var(--gray-300);
        box-sizing: border-box;
        height: 32px;
        background-color: var(--gray-100);
    }
`
const TableDataContainer = styled.div`
    width: 100%;
    border-radius: 3px;
    margin: 0 auto;
    height: 361px;
    .Select-menu-outer {
        z-index: 10000000000000000000;
    }

    .dataContainer {
        border: 1px solid var(--gray-300);
    }
    .scrollBar {
        max-height: 280px;
        overflow: auto;
        scrollbar-color: var(--gray-600);
        scrollbar-width: 3px;
        &::-webkit-scrollbar {
            width: 3px;
        }
        &::-webkit-scrollbar-track {
            width: 3px;
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            width: 3px;
            background: var(--gray-600);
            border-radius: 100px;
        }
        &::-webkit-scrollbar-track-piece {
            background: transparent;
        }
    }

    .addLine {
        width: 100%;
        border: none;
        color: var(--blue-500);
        background: var(--blue-200);
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        font-size: 14px;
        padding: 8px;
    }
    .table {
        height: 40px;
        border-bottom: 1px solid var(--gray-300);
        display: grid;
        grid-template-columns: 35% 20% 20% 20% 5%;
        background: var(--gray-100);
        &--column {
            display: flex;
            border-right: 1px solid var(--gray-300);
            padding: 8px 0px 8px 12px;
            color: var(--gray-900);
            font-size: 12px;
            justify-content: space-between;
            align-items: center;
            i {
                justify-content: center;
                align-items: center;
                padding-right: 5px;

                color: var(--blue-500);
            }
            &__data {
                color: var(--gray-800);
                background: var(--gray-0);
                border-right: 1px solid var(--gray-300);
                font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
                font-size: 12px;
                height: 39px;
                input {
                    color: var(--gray-800);
                    width: 100%;
                    height: 39px;
                    border: none;
                    font-size: 14px;
                    background-color: var(--gray-0);
                }
                &--select {
                    border: none;
                    background: var(--gray-0) !important;
                    height: 39px;
                    margin-bottom: none;
                    select {
                        border: 1px solid green;
                    }
                    i {
                        color: var(--blue-500);
                    }
                }
            }
        }
        &--lastColumn {
            button {
                background: var(--gray-100);
                border: none;
                margin: 0 auto;
                width: 100%;
                height: 39px;
            }
            i {
                font-size: 12px;
                color: var(--red-500);
            }
            &__data {
                height: 39px;
            }
        }
    }
`
const FooterContainer = styled.div`
    .button {
        &--save {
            width: 79px;
            height: 32px;
            color: var(--gray-0);
            background: var(--blue-500);
            border: none;
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
            margin-right: 16px;
            &__disabled {
                width: 79px;
                height: 32px;
                color: var(--blue-200);
                background: var(--blue-400);
                border: none;
                border-radius: 3px;
                font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
                margin-right: 16px;
            }
        }
        &--cancel {
            width: 79px;
            height: 32px;
            color: var(--static-gray-0);
            background: var(--static-gray-600);
            border: 1px solid var(--gray-300);
            border-radius: 3px;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        }
    }
`
const AlertContainer = styled.div`
    .title {
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        font-weight: 600;
        font-size: 16px;
        line-height: 16px;
        color: var(--gray-0);
        margin-bottom: 8px;
        i {
            font-size: 14px;
            margin-right: 8px;
        }
    }
    .subTitle {
        margin: auto;
        font-size: 14px;
        line-height: 16px;
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        color: var(--gray-0);
    }
`
